import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, MapPin, Phone, Mail, Clock } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { submitInquiry } from '../api/api';

const BookingForm = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await submitInquiry({
        name: formData.name,
        email: formData.email,
        phone: 'N/A',
        date: new Date().toISOString().split('T')[0],
        message: formData.message,
        spaceType: 'general-inquiry'
      });

      toast({
        title: 'Message Sent!',
        description: "We'll get back to you soon."
      });

      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Submission error:', error);
      toast({
        title: 'Submission Failed',
        description: 'Please try again or email us directly at thevibe249@gmail.com',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="booking" data-testid="booking-section" className="bg-[#f4ede3] px-6 py-24 sm:px-12 sm:py-32">
      <div className="mx-auto max-w-6xl">
        <div className="grid items-start gap-14 lg:grid-cols-2 lg:gap-20">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="font-body text-[11px] font-medium uppercase tracking-[0.45em] text-[#c96f1e]">
              Get In Touch
            </p>
            <h2 className="font-display mt-4 text-[clamp(2.6rem,6vw,4.5rem)] font-semibold leading-[1.02] tracking-tight text-[#14100d]">
              Book the <span className="italic text-[#c96f1e]">space</span>
            </h2>
            <p className="font-body mt-5 max-w-lg text-base leading-relaxed text-[#4d453c] sm:text-lg">
              Rent the café, meeting space, or commercial kitchen — or just say hello.
              We'll get back to you within a day.
            </p>

            <div className="font-body mt-12 space-y-7">
              <div className="flex items-start gap-4">
                <MapPin size={20} className="mt-0.5 shrink-0 text-[#c96f1e]" />
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-[#14100d]">Address</h3>
                  <p className="mt-1 text-[#4d453c]">249 High Street, Pottstown, PA 19464</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Phone size={20} className="mt-0.5 shrink-0 text-[#c96f1e]" />
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-[#14100d]">Phone</h3>
                  <a href="tel:484-373-8016" className="mt-1 block text-[#4d453c] transition-colors hover:text-[#c96f1e]">
                    484-373-8016
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Mail size={20} className="mt-0.5 shrink-0 text-[#c96f1e]" />
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-[#14100d]">Email</h3>
                  <a href="mailto:thevibe249@gmail.com" className="mt-1 block text-[#4d453c] transition-colors hover:text-[#c96f1e]">
                    thevibe249@gmail.com
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Clock size={20} className="mt-0.5 shrink-0 text-[#c96f1e]" />
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-[#14100d]">Cafe Hours</h3>
                  <p className="mt-1 text-[#4d453c]">Mon – Thu, Sat · 10:00 AM – 6:00 PM</p>
                  <p className="text-[#4d453c]">Friday · 11:00 AM – 7:00 PM</p>
                  <p className="mt-1 text-sm text-[#4d453c]/80">Space rentals available outside cafe hours.</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            id="contact"
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
            className="rounded-2xl bg-[#14100d] p-8 shadow-2xl sm:p-10"
          >
            <h3 className="font-display text-3xl font-semibold text-[#f4ede3]">Send an inquiry</h3>
            <p className="font-body mt-2 text-sm text-[#b8ac9c]">Tell us what you're planning.</p>

            <form onSubmit={handleSubmit} className="mt-8 space-y-6">
              <div>
                <Label htmlFor="name" className="font-body text-xs font-medium uppercase tracking-[0.2em] text-[#b8ac9c]">
                  First Name *
                </Label>
                <Input
                  id="name"
                  name="name"
                  data-testid="booking-name-input"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  className="font-body mt-2 border-[#f4ede3]/15 bg-[#1d1712] text-[#f4ede3] placeholder:text-[#b8ac9c]/50 focus-visible:ring-[#f2a33c]"
                />
              </div>

              <div>
                <Label htmlFor="email" className="font-body text-xs font-medium uppercase tracking-[0.2em] text-[#b8ac9c]">
                  Email *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  data-testid="booking-email-input"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  className="font-body mt-2 border-[#f4ede3]/15 bg-[#1d1712] text-[#f4ede3] placeholder:text-[#b8ac9c]/50 focus-visible:ring-[#f2a33c]"
                />
              </div>

              <div>
                <Label htmlFor="message" className="font-body text-xs font-medium uppercase tracking-[0.2em] text-[#b8ac9c]">
                  Type your message here...
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  data-testid="booking-message-input"
                  value={formData.message}
                  onChange={handleChange}
                  rows={6}
                  disabled={isSubmitting}
                  className="font-body mt-2 border-[#f4ede3]/15 bg-[#1d1712] text-[#f4ede3] placeholder:text-[#b8ac9c]/50 focus-visible:ring-[#f2a33c]"
                />
              </div>

              <button
                type="submit"
                data-testid="booking-submit-button"
                disabled={isSubmitting}
                className="btn-pill font-body w-full justify-center disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmitting ? (
                  'Sending...'
                ) : (
                  <>
                    Submit
                    <Send size={16} />
                  </>
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default BookingForm;
